/**
 * Hooks for polling and fetching decompose suggestions.
 *
 * useDecomposeSuggestions  — polls the list endpoint every 30s (all pending suggestions for the user)
 * useDecomposeSuggestionForTask — single-task fetch, no polling
 */

import { useQuery } from '@tanstack/react-query';
import {
  getPendingDecomposeSuggestions,
  type DecomposeSuggestion,
} from '../api/ai';

export function useDecomposeSuggestions() {
  const { data, isLoading, refetch } = useQuery<DecomposeSuggestion[]>({
    queryKey: ['decompose-suggestions'],
    queryFn: getPendingDecomposeSuggestions,
    staleTime: 25_000,
    refetchInterval: 30_000,
    // Silently return empty array on error (backend may not have this route yet)
    placeholderData: [],
  });

  return {
    // Ensure always an array even if API shape changes — prevents "is not iterable" crashes
    suggestions: Array.isArray(data) ? data : [],
    isLoading,
    refetch,
  };
}

export function useDecomposeSuggestionForTask(taskId: string) {
  // Derive the per-task suggestion from the already-polled list query rather than
  // firing GET /decompose-suggestions/:taskId. That endpoint intentionally 404s
  // for every task without a pending suggestion (the common case) — the error was
  // always swallowed so the app worked, but it spammed the devtools network tab on
  // every task open. The list endpoint already returns every pending suggestion
  // for the user, each tagged with its taskId, so a client-side filter yields the
  // same result with zero extra requests.
  const { suggestions, isLoading } = useDecomposeSuggestions();

  return {
    suggestion: taskId ? (suggestions.find((s) => s.taskId === taskId) ?? null) : null,
    isLoading,
  };
}
